import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FaCity, FaArrowLeft, FaBook, FaLightbulb, FaCheck, FaTimes, FaStar } from 'react-icons/fa';
import { updateTehranLessonVocabularyAudioUrls } from '../utils/tehranLessonAudioMapper';
import AudioButton from '../components/AudioButton';

interface DialogueLine {
  person: string;
  farsi: string;
  english: string;
  transliteration?: string;
  audio?: string;
}

interface VocabularyItem {
  farsi: string;
  english: string;
  transliteration: string;
  audio?: string;
}

interface QuizQuestion {
  question: string;
  choices: string[];
  correct: number;
  explanation?: string;
}

interface TehranLesson {
  _id: string;
  title: string;
  description: string;
  category: string;
  difficulty: string;
  dialogue: DialogueLine[];
  vocabulary: VocabularyItem[];
  quiz: QuizQuestion[];
  culturalNotes: string[];
  audioUrl?: string;
  imageUrl?: string;
}

const TehranLessonDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [lesson, setLesson] = useState<TehranLesson | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentDialogueIndex, setCurrentDialogueIndex] = useState(0);
  const [showTransliteration, setShowTransliteration] = useState(false);
  const [showTranslation, setShowTranslation] = useState(true);
  const [quizAnswers, setQuizAnswers] = useState<number[]>([]);
  const [quizSubmitted, setQuizSubmitted] = useState(false);
  const [activeTab, setActiveTab] = useState<'dialogue' | 'vocabulary' | 'quiz' | 'cultural'>('dialogue');

  const fetchLesson = useCallback(async () => {
    try {
      const response = await fetch(`/api/cultural/tehran/${id}`);
      const data = await response.json();
      if (data.success) {
        const foundLesson = data.data._id === id || data.data.id === id ? data.data : null;
        if (foundLesson) {
          // Update vocabulary with correct audio URLs
          const updatedLesson = {
            ...foundLesson,
            vocabulary: updateTehranLessonVocabularyAudioUrls(foundLesson.vocabulary)
          };
          setLesson(updatedLesson);
          setQuizAnswers(new Array(foundLesson.quiz.length).fill(-1));
        } else {
          setLesson(null);
        }
      }
    } catch (error) {
      console.error('Error fetching lesson:', error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      fetchLesson();
    }
  }, [id, fetchLesson]);

  const handleQuizAnswer = (questionIndex: number, answerIndex: number) => {
    if (!quizSubmitted) {
      const newAnswers = [...quizAnswers];
      newAnswers[questionIndex] = answerIndex;
      setQuizAnswers(newAnswers);
    }
  };

  const submitQuiz = () => {
    setQuizSubmitted(true);
  };

  const getQuizScore = () => {
    if (!lesson) return 0;
    let correct = 0;
    lesson.quiz.forEach((question, index) => {
      if (quizAnswers[index] === question.correct) {
        correct++;
      }
    });
    return Math.round((correct / lesson.quiz.length) * 100);
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'beginner': return 'bg-emerald-100 text-emerald-900 border-2 border-emerald-300 dark:bg-emerald-900/30 dark:text-emerald-100 dark:border-emerald-600';
      case 'intermediate': return 'bg-amber-100 text-amber-900 border-2 border-amber-300 dark:bg-amber-900/30 dark:text-amber-100 dark:border-amber-600';
      case 'advanced': return 'bg-rose-100 text-rose-900 border-2 border-rose-300 dark:bg-rose-900/30 dark:text-rose-100 dark:border-rose-600';
      default: return 'bg-slate-100 text-slate-900 border-2 border-slate-300 dark:bg-slate-800/30 dark:text-slate-100 dark:border-slate-600';
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'cultural': return 'bg-gradient-to-r from-purple-100 to-indigo-100 text-purple-900 border-2 border-purple-300 dark:bg-gradient-to-r dark:from-purple-900/30 dark:to-indigo-900/30 dark:text-purple-100 dark:border-purple-600';
      case 'shopping': return 'bg-gradient-to-r from-blue-100 to-cyan-100 text-blue-900 border-2 border-blue-300 dark:bg-gradient-to-r dark:from-blue-900/30 dark:to-cyan-900/30 dark:text-blue-100 dark:border-blue-600';
      case 'transport': return 'bg-gradient-to-r from-teal-100 to-emerald-100 text-teal-900 border-2 border-teal-300 dark:bg-gradient-to-r dark:from-teal-900/30 dark:to-emerald-900/30 dark:text-teal-100 dark:border-teal-600';
      case 'restaurant': return 'bg-gradient-to-r from-orange-100 to-red-100 text-orange-900 border-2 border-orange-300 dark:bg-gradient-to-r dark:from-orange-900/30 dark:to-red-900/30 dark:text-orange-100 dark:border-orange-600';
      case 'social': return 'bg-gradient-to-r from-pink-100 to-rose-100 text-pink-900 border-2 border-pink-300 dark:bg-gradient-to-r dark:from-pink-900/30 dark:to-rose-900/30 dark:text-pink-100 dark:border-pink-600';
      case 'heritage': return 'bg-gradient-to-r from-amber-100 to-yellow-100 text-amber-900 border-2 border-amber-300 dark:bg-gradient-to-r dark:from-amber-900/30 dark:to-yellow-900/30 dark:text-amber-100 dark:border-amber-600';
      default: return 'bg-gradient-to-r from-slate-100 to-gray-100 text-slate-900 border-2 border-slate-300 dark:bg-gradient-to-r dark:from-slate-800/30 dark:to-gray-800/30 dark:text-slate-100 dark:border-slate-600';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50 dark:from-gray-900 dark:via-purple-900 dark:to-gray-800">
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!lesson) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50 dark:from-gray-900 dark:via-purple-900 dark:to-gray-800">
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-800 dark:text-white mb-4">
              Lesson not found
            </h1>
            <Link
              to="/cultural/tehran"
              className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg hover:from-purple-600 hover:to-pink-600 transition-all duration-300 shadow-lg"
            >
              <FaArrowLeft className="mr-2" />
              Back to Lessons
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50 dark:from-gray-900 dark:via-purple-900 dark:to-gray-800">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/cultural/tehran"
            className="inline-flex items-center text-purple-600 hover:text-purple-700 mb-4 transition-colors duration-200"
          >
            <FaArrowLeft className="mr-2" />
            Back to Lessons
          </Link>
          
          <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-6 shadow-xl border border-white/20">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <h1 className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent mb-2">
                  {lesson.title}
                </h1>
                <p className="text-gray-700 dark:text-gray-200 mb-4 text-lg leading-relaxed">
                  {lesson.description}
                </p>
                <div className="flex flex-wrap gap-2">
                  <span className={`px-4 py-2 rounded-full text-sm font-semibold shadow-sm ${getCategoryColor(lesson.category)}`}>
                    {lesson.category}
                  </span>
                  <span className={`px-4 py-2 rounded-full text-sm font-semibold shadow-sm ${getDifficultyColor(lesson.difficulty)}`}>
                    {lesson.difficulty}
                  </span>
                </div>
              </div>
              <div className="w-16 h-16 rounded-xl bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 flex items-center justify-center text-white shadow-lg">
                <FaCity className="text-2xl" />
              </div>
            </div>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-2 shadow-xl border border-white/20 mb-8">
          <div className="flex space-x-2">
            {[
              { id: 'dialogue', label: 'Dialogue', icon: FaBook, color: 'from-purple-500 to-purple-600' },
              { id: 'vocabulary', label: 'Vocabulary', icon: FaStar, color: 'from-pink-500 to-pink-600' },
              { id: 'quiz', label: 'Quiz', icon: FaCheck, color: 'from-orange-500 to-orange-600' },
              { id: 'cultural', label: 'Cultural Notes', icon: FaLightbulb, color: 'from-indigo-500 to-indigo-600' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex-1 flex items-center justify-center px-4 py-3 rounded-xl font-medium transition-all duration-300 ${
                  activeTab === tab.id
                    ? `bg-gradient-to-r ${tab.color} text-white shadow-lg transform scale-105`
                    : 'text-white hover:bg-gray-100 dark:hover:bg-gray-700 hover:scale-105'
                }`}
              >
                <tab.icon className="mr-2" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-6 shadow-xl border border-white/20">
          {/* Dialogue Tab */}
          {activeTab === 'dialogue' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                  Interactive Dialogue
                </h2>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setShowTransliteration(!showTransliteration)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                      showTransliteration
                        ? 'bg-gradient-to-r from-purple-500 to-purple-600 text-white shadow-md hover:from-purple-600 hover:to-purple-700'
                        : 'bg-white dark:bg-gray-700 text-purple-600 dark:text-purple-400 border-2 border-purple-300 dark:border-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/20 hover:border-purple-400 dark:hover:border-purple-500'
                    }`}
                  >
                    Transliteration
                  </button>
                  <button
                    onClick={() => setShowTranslation(!showTranslation)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                      showTranslation
                        ? 'bg-gradient-to-r from-pink-500 to-pink-600 text-white shadow-md hover:from-pink-600 hover:to-pink-700'
                        : 'bg-white dark:bg-gray-700 text-pink-600 dark:text-pink-400 border-2 border-pink-300 dark:border-pink-600 hover:bg-pink-50 dark:hover:bg-pink-900/20 hover:border-pink-400 dark:hover:border-pink-500'
                    }`}
                  >
                    Translation
                  </button>
                </div>
              </div>

              <div className="space-y-4">
                {lesson.dialogue.map((line, index) => (
                  <div
                    key={index}
                    className={`p-4 rounded-lg border-2 transition-all duration-300 ${
                      index === currentDialogueIndex
                        ? 'border-purple-500 bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 shadow-lg transform scale-105'
                        : 'border-gray-200 dark:border-gray-700 hover:border-purple-300 dark:hover:border-purple-600 hover:shadow-md'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="font-semibold text-purple-700 dark:text-purple-300 mb-2">
                          {line.person}
                        </div>
                        <div className="text-lg text-gray-900 dark:text-white mb-2 font-medium">
                          {line.farsi}
                        </div>
                        {showTransliteration && line.transliteration && (
                          <div className="text-sm text-gray-600 dark:text-gray-400 mb-2 italic">
                            {line.transliteration}
                          </div>
                        )}
                        {showTranslation && (
                          <div className="text-gray-700 dark:text-gray-300 font-medium">
                            "{line.english}"
                          </div>
                        )}
                      </div>
                      <AudioButton
                        audioUrl={line.audio}
                        text={line.farsi}
                        className="ml-4"
                      />
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex justify-center mt-6">
                <div className="flex space-x-2">
                  <button
                    onClick={() => setCurrentDialogueIndex(Math.max(0, currentDialogueIndex - 1))}
                    disabled={currentDialogueIndex === 0}
                    className="px-4 py-2 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-lg disabled:opacity-50 hover:from-purple-600 hover:to-purple-700 transition-all duration-200 shadow-md"
                  >
                    Previous
                  </button>
                  <span className="px-4 py-2 text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 rounded-lg font-medium">
                    {currentDialogueIndex + 1} of {lesson.dialogue.length}
                  </span>
                  <button
                    onClick={() => setCurrentDialogueIndex(Math.min(lesson.dialogue.length - 1, currentDialogueIndex + 1))}
                    disabled={currentDialogueIndex === lesson.dialogue.length - 1}
                    className="px-4 py-2 bg-gradient-to-r from-pink-500 to-pink-600 text-white rounded-lg disabled:opacity-50 hover:from-pink-600 hover:to-pink-700 transition-all duration-200 shadow-md"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Vocabulary Tab */}
          {activeTab === 'vocabulary' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold bg-gradient-to-r from-pink-600 to-orange-600 bg-clip-text text-transparent">
                  Key Vocabulary
                </h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {lesson.vocabulary.map((item, index) => (
                  <div
                    key={index}
                    className="p-4 border border-pink-200 dark:border-pink-700 rounded-lg hover:bg-gradient-to-r hover:from-pink-50 hover:to-orange-50 dark:hover:from-pink-900/20 dark:hover:to-orange-900/20 transition-all duration-300 hover:shadow-md hover:scale-105"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="text-lg font-semibold text-pink-700 dark:text-pink-300 mb-1">
                          {item.farsi}
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400 mb-1 italic">
                          {item.transliteration}
                        </div>
                        <div className="text-gray-700 dark:text-gray-300 font-medium">
                          {item.english}
                        </div>
                      </div>
                      <AudioButton
                        audioUrl={item.audio}
                        text={item.farsi}
                        variant="accent"
                        className="ml-4"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Quiz Tab */}
          {activeTab === 'quiz' && (
            <div>
              <h2 className="text-2xl font-bold bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent mb-6">
                Practice Quiz
              </h2>
              {!quizSubmitted && (
                <div className="space-y-6">
                  {lesson.quiz.map((question, questionIndex) => (
                    <div key={questionIndex} className="p-4 border border-orange-200 dark:border-orange-700 rounded-lg bg-gradient-to-r from-orange-50/50 to-red-50/50 dark:from-orange-900/20 dark:to-red-900/20">
                      <h3 className="font-semibold text-orange-700 dark:text-orange-300 mb-4">
                        {questionIndex + 1}. {question.question}
                      </h3>
                      <div className="space-y-2">
                        {question.choices.map((choice, choiceIndex) => (
                          <button
                            key={choiceIndex}
                            onClick={() => handleQuizAnswer(questionIndex, choiceIndex)}
                            className={`w-full text-left p-4 rounded-lg border-2 transition-all duration-200 font-medium ${
                              quizAnswers[questionIndex] === choiceIndex
                                ? 'border-orange-500 bg-gradient-to-r from-orange-50 to-red-50 dark:from-orange-900/30 dark:to-red-900/30 shadow-md transform scale-105 text-orange-800 dark:text-orange-200'
                                : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:border-orange-400 dark:hover:border-orange-500 hover:bg-orange-50 dark:hover:bg-orange-900/20 hover:shadow-sm hover:text-orange-700 dark:hover:text-orange-300'
                            }`}
                          >
                            {choice}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                  <button
                    onClick={submitQuiz}
                    className="w-full py-3 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-lg hover:from-orange-600 hover:to-red-600 transition-all duration-200 shadow-lg font-medium"
                  >
                    Submit Quiz
                  </button>
                </div>
              )}

              {quizSubmitted && (
                <div className="space-y-6">
                  <div className="text-center p-6 bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 rounded-2xl text-white shadow-xl">
                    <h3 className="text-2xl font-bold mb-2">Quiz Results</h3>
                    <div className="text-4xl font-bold mb-2">{getQuizScore()}%</div>
                    <p className="text-purple-100">
                      You got {quizAnswers.filter((answer, index) => answer === lesson.quiz[index].correct).length} out of {lesson.quiz.length} correct!
                    </p>
                  </div>

                  {lesson.quiz.map((question, questionIndex) => (
                    <div key={questionIndex} className="p-4 border border-indigo-200 dark:border-indigo-700 rounded-lg bg-gradient-to-r from-indigo-50/50 to-purple-50/50 dark:from-indigo-900/20 dark:to-purple-900/20">
                      <h3 className="font-semibold text-indigo-700 dark:text-indigo-300 mb-4">
                        {questionIndex + 1}. {question.question}
                      </h3>
                      <div className="space-y-2">
                        {question.choices.map((choice, choiceIndex) => (
                          <div
                            key={choiceIndex}
                            className={`p-4 rounded-lg border-2 flex items-center justify-between transition-all duration-200 font-medium ${
                              choiceIndex === question.correct
                                ? 'border-emerald-500 bg-gradient-to-r from-emerald-50 to-green-50 dark:from-emerald-900/30 dark:to-green-900/30 shadow-md text-emerald-800 dark:text-emerald-200'
                                : quizAnswers[questionIndex] === choiceIndex
                                ? 'border-rose-500 bg-gradient-to-r from-rose-50 to-red-50 dark:from-rose-900/30 dark:to-red-900/30 shadow-md text-rose-800 dark:text-rose-200'
                                : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:border-indigo-400 dark:hover:border-indigo-500'
                            }`}
                          >
                            <span>{choice}</span>
                            {choiceIndex === question.correct && (
                              <FaCheck className="text-emerald-500 text-lg" />
                            )}
                            {quizAnswers[questionIndex] === choiceIndex && choiceIndex !== question.correct && (
                              <FaTimes className="text-rose-500 text-lg" />
                            )}
                          </div>
                        ))}
                      </div>
                      {question.explanation && (
                        <div className="mt-3 p-3 bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 rounded-lg">
                          <p className="text-sm text-gray-700 dark:text-gray-300">
                            <strong>Explanation:</strong> {question.explanation}
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Cultural Notes Tab */}
          {activeTab === 'cultural' && (
            <div>
              <h2 className="text-2xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent mb-6">
                Cultural Insights
              </h2>
              <div className="space-y-4">
                {lesson.culturalNotes.map((note, index) => (
                  <div
                    key={index}
                    className="p-6 bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/30 dark:to-purple-900/30 border border-indigo-200 dark:border-indigo-600 rounded-xl hover:shadow-lg transition-all duration-200"
                  >
                    <div className="flex items-start">
                      <FaLightbulb className="text-indigo-600 dark:text-indigo-400 mt-1 mr-4 flex-shrink-0 text-lg" />
                      <p className="text-gray-800 dark:text-gray-100 leading-relaxed font-medium" style={{ direction: 'ltr', textAlign: 'left' }}>
                        {note}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TehranLessonDetail; 